import axios from 'axios';
import { supabase } from './supabase';

// Cache the session token to avoid calling getSession() on every request
let cachedToken = null;
let tokenExpiresAt = 0;

// Listen for auth state changes to keep cache in sync
supabase.auth.onAuthStateChange((_event, session) => {
  if (session?.access_token) {
    cachedToken = session.access_token;
    tokenExpiresAt = (session.expires_at || 0) * 1000; // convert to ms
  } else {
    cachedToken = null;
    tokenExpiresAt = 0;
  }
});

// Attach Supabase JWT to every request using cached token
axios.interceptors.request.use(async (config) => {
  // Use cached token if still valid (with 120s buffer)
  config.timeout = config.timeout || 30000;
  if (cachedToken && Date.now() < tokenExpiresAt - 120000) {
    config.headers.Authorization = `Bearer ${cachedToken}`;
    return config;
  }
  // Otherwise refresh
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    cachedToken = session.access_token;
    tokenExpiresAt = (session.expires_at || 0) * 1000;
    config.headers.Authorization = `Bearer ${session.access_token}`;
  }
  return config;
});

// On 401, try to refresh the token once before signing out
let isRefreshing = false;
let refreshPromise = null;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only handle 401 and only retry once
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If already refreshing, wait for that to finish
      if (isRefreshing) {
        try {
          const token = await refreshPromise;
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        } catch {
          return Promise.reject(error);
        }
      }

      isRefreshing = true;
      refreshPromise = (async () => {
        try {
          const { data, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError || !data.session) {
            throw new Error('Refresh failed');
          }
          cachedToken = data.session.access_token;
          tokenExpiresAt = (data.session.expires_at || 0) * 1000;
          return data.session.access_token;
        } catch {
          // Only sign out if refresh truly fails (token is invalid/expired beyond recovery)
          cachedToken = null;
          tokenExpiresAt = 0;
          await supabase.auth.signOut();
          throw new Error('Session expired');
        } finally {
          isRefreshing = false;
        }
      })();

      try {
        const newToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default axios;

// Safe data helpers — ensure API responses are always the expected type
export const safeArray = (data) => Array.isArray(data) ? data : [];
export const safeObject = (data) => (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
